import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Smartphone, Download, Check, Zap, Wifi, 
  Bell, Lock, ArrowRight, Share, Menu, Plus, ArrowLeft 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import Footer from '@/components/Footer';

const InstallPWA = () => {
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    const android = /Android/.test(navigator.userAgent);
    setIsAndroid(android);

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setIsInstalled(true);
      setTimeout(() => navigate('/'), 2000);
    }

    setDeferredPrompt(null);
  };

  const features = [
    {
      icon: Zap,
      title: 'Acceso Instantáneo',
      description: 'Abre la app desde tu pantalla de inicio en menos de 1 segundo sin abrir navegador'
    },
    {
      icon: Wifi,
      title: 'Modo Offline',
      description: 'Consulta tus datos y revisa tu estado financiero incluso en zonas sin cobertura'
    },
    {
      icon: Bell,
      title: 'Recordatorios',
      description: 'Recibe alertas oportunas de fechas de corte y pagos de tarjetas y deudas'
    },
    {
      icon: Lock,
      title: 'Máxima Privacidad',
      description: 'Entorno de ejecución aislado y seguro como cualquier aplicación nativa'
    }
  ];

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col font-sans">
      {/* Header */}
      <header className="bg-stone-50/90 backdrop-blur-sm border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 sm:gap-2.5 hover:opacity-75 transition-opacity duration-150 flex-shrink-0">
              <img 
                src="/icon-192x192.png" 
                alt="PGastos" 
                className="h-7 sm:h-8 w-auto"
              />
              <span className="text-base sm:text-lg font-bold text-stone-900 tracking-tight">PGastos</span>
            </Link>
            <Link to="/">
              <Button variant="outline" size="sm" className="border-stone-300 hover:bg-stone-100 text-stone-700 text-xs sm:text-sm active:scale-95 transition-all duration-150 ease-spring cursor-pointer">
                <ArrowLeft className="w-4 h-4 mr-1.5" />
                Volver
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 w-full">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-stone-900 rounded-2xl mb-6 shadow-sm">
            <Smartphone className="w-8 h-8 text-indigo-400" />
          </div>
          <h1 className="text-3xl sm:text-5xl font-black text-stone-900 mb-4 tracking-tight">
            Instala PGastos en tu celular
          </h1>
          <p className="text-base sm:text-lg text-stone-600 max-w-xl mx-auto font-medium leading-relaxed">
            Ten la experiencia completa de una app instalada sin ocupar gigabytes en tu almacenamiento.
          </p>
        </div>

        {/* Install State / Instructions */}
        {isInstalled ? (
          <Card className="bg-white border-2 border-emerald-500 rounded-3xl p-8 mb-12 text-center shadow-sm">
            <div className="w-12 h-12 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center mx-auto mb-3">
              <Check className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-black text-stone-900 mb-2">¡App ya instalada!</h3>
            <p className="text-sm text-stone-600 mb-6 font-medium">
              Ya tienes PGastos en tu dispositivo. Abre el ícono desde tu pantalla de inicio.
            </p>
            <Link to="/">
              <Button size="lg" className="bg-stone-900 hover:bg-stone-800 text-white font-bold px-8 py-4 rounded-xl active:scale-95 transition-all duration-150 ease-spring">
                Ir a la App
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </Card>
        ) : deferredPrompt ? (
          <Card className="bg-stone-900 border border-stone-800 rounded-3xl p-8 sm:p-12 mb-12 text-center text-white shadow-xl">
            <h3 className="text-2xl sm:text-3xl font-black mb-3 tracking-tight">¡Instala en 1 Toque!</h3>
            <p className="text-sm text-stone-400 mb-8 max-w-md mx-auto font-medium">
              Pulsa el botón para añadir el acceso directo con icono oficial a tu pantalla principal.
            </p>
            <Button
              onClick={handleInstallClick}
              size="lg"
              className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-base px-8 py-6 rounded-2xl active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring shadow-lg cursor-pointer"
            >
              <Download className="w-5 h-5 mr-2" />
              Instalar Ahora
            </Button>
          </Card>
        ) : isIOS ? (
          <Card className="bg-white border border-stone-200 rounded-3xl p-7 sm:p-9 mb-12 shadow-sm">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-stone-100">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <h3 className="text-lg font-black text-stone-900">Instalación en iPhone / iPad (Safari)</h3>
                <p className="text-xs text-stone-500 font-medium">Sigue estos 3 sencillos pasos:</p>
              </div>
            </div>

            <ol className="space-y-4">
              <li className="flex items-start gap-3.5">
                <span className="flex-shrink-0 w-7 h-7 bg-stone-900 text-white rounded-lg flex items-center justify-center text-xs font-black">1</span>
                <div>
                  <p className="text-sm font-bold text-stone-900">Toca el botón Compartir</p>
                  <div className="flex items-center gap-1.5 text-xs text-stone-500 mt-0.5 font-medium">
                    <Share className="w-3.5 h-3.5 text-indigo-600" />
                    <span>Ubicado en la barra inferior de Safari</span>
                  </div>
                </div>
              </li>
              <li className="flex items-start gap-3.5">
                <span className="flex-shrink-0 w-7 h-7 bg-stone-900 text-white rounded-lg flex items-center justify-center text-xs font-black">2</span>
                <div>
                  <p className="text-sm font-bold text-stone-900">Selecciona "Agregar a inicio"</p>
                  <div className="flex items-center gap-1.5 text-xs text-stone-500 mt-0.5 font-medium">
                    <Plus className="w-3.5 h-3.5 text-indigo-600" />
                    <span>Desliza hacia abajo en el menú de opciones</span>
                  </div>
                </div>
              </li>
              <li className="flex items-start gap-3.5">
                <span className="flex-shrink-0 w-7 h-7 bg-stone-900 text-white rounded-lg flex items-center justify-center text-xs font-black">3</span>
                <div>
                  <p className="text-sm font-bold text-stone-900">Confirma en "Agregar"</p>
                  <p className="text-xs text-stone-500 mt-0.5 font-medium">PGastos aparecerá como una app en tu pantalla con su icono oficial.</p>
                </div>
              </li>
            </ol>
          </Card>
        ) : isAndroid ? (
          <Card className="bg-white border border-stone-200 rounded-3xl p-7 sm:p-9 mb-12 shadow-sm">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-stone-100">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <h3 className="text-lg font-black text-stone-900">Instalación en Android (Chrome)</h3>
                <p className="text-xs text-stone-500 font-medium">Sigue estos pasos:</p>
              </div>
            </div>

            <ol className="space-y-4">
              <li className="flex items-start gap-3.5">
                <span className="flex-shrink-0 w-7 h-7 bg-stone-900 text-white rounded-lg flex items-center justify-center text-xs font-black">1</span>
                <div>
                  <p className="text-sm font-bold text-stone-900">Abre el menú de opciones (⋮)</p>
                  <p className="text-xs text-stone-500 mt-0.5 font-medium">En la esquina superior derecha del navegador Chrome</p>
                </div>
              </li>
              <li className="flex items-start gap-3.5">
                <span className="flex-shrink-0 w-7 h-7 bg-stone-900 text-white rounded-lg flex items-center justify-center text-xs font-black">2</span>
                <div>
                  <p className="text-sm font-bold text-stone-900">Toca "Instalar app" o "Agregar a pantalla principal"</p>
                  <p className="text-xs text-stone-500 mt-0.5 font-medium">Acepta la confirmación del sistema</p>
                </div>
              </li>
            </ol>
          </Card>
        ) : (
          <Card className="bg-white border border-stone-200 rounded-3xl p-7 sm:p-9 mb-12 shadow-sm">
            <div className="text-center mb-6">
              <h3 className="text-lg font-black text-stone-900">Instalación en Computadora (Chrome / Edge)</h3>
              <p className="text-xs text-stone-500 font-medium mt-1">Abre el icono de instalación (⊞) en la barra de direcciones superior.</p>
            </div>
          </Card>
        )}

        {/* Benefits Grid */}
        <h2 className="text-2xl font-black text-stone-900 mb-6 text-center tracking-tight">
          Ventajas de la versión instalada
        </h2>
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {features.map((feature, idx) => (
            <Card key={idx} className="p-6 bg-white border border-stone-200 rounded-2xl shadow-xs hover:shadow-sm transition-all duration-150 ease-spring">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4">
                <feature.icon className="w-5 h-5 text-indigo-400" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1">
                {feature.title}
              </h3>
              <p className="text-xs sm:text-sm text-stone-500 leading-relaxed font-medium">
                {feature.description}
              </p>
            </Card>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link to="/?register=true">
            <Button size="lg" className="bg-stone-900 hover:bg-stone-800 text-white font-bold px-8 py-4 rounded-xl active:scale-95 transition-all duration-150 ease-spring cursor-pointer shadow-sm">
              Crear Cuenta o Iniciar Sesión
            </Button>
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default InstallPWA;
